/**
 * <variant-radios>
 * Wraps option inputs (radio or select) and resolves the matching variant
 * from the JSON in data-product-variants on every change.
 */
class VariantRadios extends HTMLElement {
  constructor() {
    super();
    this._variants = [];
    this._onChange = this._onChange.bind(this);
  }

  connectedCallback() {
    this._variants = this._readVariants();
    this.addEventListener('change', this._onChange);
    // Initial sync — useful when arriving with ?variant=X already selected.
    queueMicrotask(() => this._sync(false));
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._onChange);
  }

  _readVariants() {
    const raw = this.getAttribute('data-product-variants');
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  _onChange(e) {
    if (!e.target || !e.target.matches('input, select')) return;
    this._sync(true);
  }

  _sync(notify) {
    const selected = this._collectSelectedOptions();
    const variant = this._matchVariant(selected);
    if (!variant) {
      this._toggleSoldOut(true, true);
      return;
    }

    // Update the parent form's hidden id input.
    const form = this.closest('product-form') || document;
    const idInput = form.querySelector ? form.querySelector('[name="id"]') : null;
    if (idInput) idInput.value = String(variant.id);

    // Update price displays scoped to nearest section, or globally.
    const priceTargets = document.querySelectorAll('[data-variant-price]');
    priceTargets.forEach((el) => { el.textContent = this._formatMoney(variant.price); });

    // Update gallery.
    if (variant.featured_image) {
      const gallery = document.querySelector('product-gallery');
      if (gallery && typeof gallery.setFeaturedImage === 'function') {
        gallery.setFeaturedImage(variant.featured_image);
      } else {
        const img = document.querySelector('[data-product-image]');
        const src = typeof variant.featured_image === 'string'
          ? variant.featured_image
          : (variant.featured_image.src || '');
        if (img && src) img.setAttribute('src', src);
      }
    }

    // Sold out indicator.
    this._toggleSoldOut(!variant.available, false);

    // Update URL.
    this._writeVariantToUrl(variant.id);

    if (notify) {
      this.dispatchEvent(new CustomEvent('variant:changed', {
        bubbles: true,
        detail: { variant }
      }));
    }
  }

  _collectSelectedOptions() {
    const values = [];
    // Group inputs by name (option1/option2/option3, or any name).
    const groups = new Map();
    const inputs = this.querySelectorAll('input[type="radio"], input[type="checkbox"], select');
    inputs.forEach((el) => {
      const name = el.name || el.dataset.optionName || '';
      if (!name) return;
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(el);
    });

    groups.forEach((els) => {
      // Prefer the first checked radio; for a select use its value.
      let chosen = '';
      els.forEach((el) => {
        if (el.tagName === 'SELECT') {
          chosen = el.value;
        } else if ((el.type === 'radio' || el.type === 'checkbox') && el.checked) {
          chosen = el.value;
        }
      });
      values.push(chosen);
    });

    return values;
  }

  _matchVariant(selected) {
    if (!this._variants.length) return null;
    return this._variants.find((v) => {
      return selected.every((val, i) => {
        const key = `option${i + 1}`;
        return v[key] === val;
      });
    }) || null;
  }

  _toggleSoldOut(soldOut, unavailable) {
    const indicators = document.querySelectorAll('[data-sold-out]');
    indicators.forEach((el) => {
      el.hidden = !soldOut;
      if (soldOut) el.textContent = unavailable ? 'No disponible' : 'Sin stock';
    });

    const submit = document.querySelector('product-form [type="submit"]');
    if (submit) {
      if (soldOut) {
        submit.disabled = true;
        submit.textContent = unavailable ? 'NO DISPONIBLE' : 'SIN STOCK';
      } else {
        submit.disabled = false;
        const original = submit.dataset.defaultText || 'AÑADIR AL CARRITO';
        submit.textContent = original;
      }
    }
  }

  _writeVariantToUrl(variantId) {
    if (!variantId || typeof window === 'undefined' || !window.history) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', url.toString());
    } catch (_) { /* noop */ }
  }

  _formatMoney(cents) {
    const n = (Number(cents) || 0) / 100;
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

customElements.define('variant-radios', VariantRadios);
